/**
 * Medication Order API Service
 */

import apiClient from './apiClient';
import {
  MedicationOrderDTO,
  CreateMedicationOrderRequest,
  MedicationOrderStatus,
  PageResponse,
} from '../types/medication.types';

const BASE_PATH = '/v1/medication-orders';

class MedicationService {
  async createMedicationOrder(request: CreateMedicationOrderRequest): Promise<MedicationOrderDTO> {
    const response = await apiClient.post<MedicationOrderDTO>(BASE_PATH, request);
    return response.data;
  }

  async getMedicationOrderById(id: string): Promise<MedicationOrderDTO> {
    const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  }

  async getMedicationOrderByOrderNumber(orderNumber: string): Promise<MedicationOrderDTO> {
    const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/order-number/${orderNumber}`);
    return response.data;
  }

  async getAllMedicationOrders(page: number = 0, size: number = 20): Promise<PageResponse<MedicationOrderDTO>> {
    const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(BASE_PATH, {
      params: { page, size },
    });
    return response.data;
  }

  async getMedicationOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
    const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}`);
    return response.data;
  }

  async getActiveMedicationOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
    const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}/active`);
    return response.data;
  }

  async getMedicationOrdersByStatus(status: MedicationOrderStatus): Promise<MedicationOrderDTO[]> {
    const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/status/${status}`);
    return response.data;
  }

  async updateMedicationOrder(id: string, data: Partial<MedicationOrderDTO>): Promise<MedicationOrderDTO> {
    const response = await apiClient.put<MedicationOrderDTO>(`${BASE_PATH}/${id}`, data);
    return response.data;
  }

  async approveMedicationOrder(id: string): Promise<MedicationOrderDTO> {
    const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/approve`);
    return response.data;
  }

  async discontinueMedicationOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
    const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/discontinue`, null, {
      params: { reason },
    });
    return response.data;
  }

  async deleteMedicationOrder(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  // --- Low Stock Configuration ---
  async getLowStockConfig(): Promise<any> {
    const response = await apiClient.get('/v1/medication/low-stock-config');
    return response.data;
  }

  async saveLowStockConfig(data: { isEnabled: boolean; notifyPharmacy: boolean; notifyDoctor: boolean; reminderDays: number }): Promise<any> {
    const response = await apiClient.post('/v1/medication/low-stock-config', data);
    return response.data;
  }
}

export default new MedicationService();
